import { HttpClient } from './httpClient.ts'
import { PickPublic } from '../lib/utils.ts'
import { SubscriptionPlan } from './chatApi.ts'
import { LangLevels } from '../data/onboardingSteps.ts'
import { AppLanguages } from '../translations/messages.ts'

export interface YandexAuthData {
  token: string
  native_language?: NativeLang
}

export interface TelegramAuthData {
  id: number
  first_name: string
  auth_date: number
  hash: string
  last_name?: string
  photo_url?: string
  username?: string
  native_language?: NativeLang
}

export interface VKIDAuthData {
  native_language?: NativeLang
  id_token: string
}

export type OnbEventSuffix =
  | `level_${LangLevels}`
  | `goal_${string}`
  | `profession_${string}`
  | `onboarding_passed`
  | `trial_cancelled`
  | '1st_question'
  | 'demo_finished'
  | 'subscriptions'

type OnbEvents = `onb_${OnbEventSuffix}` | `onbdemo_${OnbEventSuffix}`

export type MarketingEvents =
  | 'finish_onboarding'
  | 'click_buy'
  | 'click_selection'
  | 'click_again'
  | 'subscribtion_page_load'
  | 'subscribtion_click'
  | 'subscribtion_trial_click'
  | 'click_plans_demo_onboarding'
  | 'click_help_demo_onboarding'
  | 'microfon_accept'
  | 'microfon_sent'
  | 'registration_page_load'
  | 'edman_intro_start'
  | 'profession_sent'
  | 'hobby_sent'
  | 'user_english_level_sent'
  | 'purpose_of_study_sent'
  | 'all_good_registration'
  | 'demo_lesson_start'
  | 'picture_demo_sent'
  | 'picture_demo_answer'
  | 'training_program_sent'
  | 'paing_link_sent'
  | 'paing_finished'
  | 'demo_popup_keep'
  | 'demo_popup_rus'
  | 'demo_level_answer'
  | 'demo_topic_answer'
  | 'demo_username_answer'
  | 'demo_subtopic_answer'
  | 'demo_complete_answer'
  | `trial_started_${SubscriptionPlan}`
  | 'demo2_level_chosen'
  | 'demo2_lesson_launched'
  | 'demo2_photo_received'
  | 'demo2_photo_answered'
  | 'demo2_text_answered'
  | 'demo2_puzzle_passed'
  | 'demo2_lesson_finished'
  | 'demo2_subscription_visit'
  | 'demo2_payment_link'
  | 'demo2_successful_payment'
  | 'tts_click'
  | OnbEvents

export interface MarkData {
  referrer_domain: string | null
  utm_content: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_term: string | null
  utm_placement: string | null
  clientID: string | null
  lang: AppLanguages | null
  geoIp?: GeoIp
}

// 0 - not rus, 1 - rus
export type GeoIp = '0' | '1'

export enum NativeLang {
  EN = 'English',
  RU = 'Russian',
  TR = 'Turkish',
  VI = 'Vietnamese',
}

export const NATIVE_LANG_MAP: Record<AppLanguages, NativeLang> = {
  en: NativeLang.EN,
  ru: NativeLang.RU,
  tr: NativeLang.TR,
  vi: NativeLang.VI,
}

export type AuthApi = PickPublic<AppAuthApi>
export class AppAuthApi {
  constructor(private httpClient: HttpClient) {}

  register(
    email: string,
    password: string,
    markData: MarkData | null,
    userUUID: string,
    language: AppLanguages,
  ) {
    const body = {
      email,
      password: password ? password : undefined,
      ...markData,
      userUUID,
      native_language: NATIVE_LANG_MAP[language],
    }
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/register/', body)
  }

  register_anon(level: LangLevels) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/register_anon/', { level })
  }

  delete_anon() {
    return this.httpClient.del<never>('/api/delete_anon/', {})
  }

  async login(email: string, password: string) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
    }>('/api/login/', { email, password })
  }

  confirm_phone(code: string) {
    return this.httpClient.post<{ status: 'success' | 'error' }>(
      '/api/confirm_phone/',
      { code },
    )
  }

  resend_code() {
    return this.httpClient.get<{ status: 'success' | 'error' }>(
      '/api/resend_code/',
    )
  }

  track_event(
    event_name: MarketingEvents,
    userId: number | null,
    edmanUserUUID: string | undefined,
  ) {
    return this.httpClient.post<never>('/api/track_event/', {
      event_name,
      event_src: 'app',
      user_id: edmanUserUUID ?? null,
      event_args: { additionalProp1: userId },
    })
  }

  reset_password(email: string) {
    return this.httpClient.post('/api/reset_password/', { email })
  }

  telegram_auth(authData: TelegramAuthData) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/smm/telegram/auth/', authData)
  }

  telegram_register(authData: TelegramAuthData) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/smm/telegram/register/', authData)
  }

  yandex_auth(data: YandexAuthData) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/smm/yandex/auth/', data)
  }

  yandex_register(data: YandexAuthData) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/smm/yandex/register/', data)
  }

  vkid_auth(authData: VKIDAuthData) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/smm/vk/auth/', authData)
  }

  vkid_register(authData: VKIDAuthData) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      sms_confirm?: boolean
    }>('/api/smm/vk/register/', authData)
  }

  google_login(code: string) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      login: string
      sms_confirm?: boolean
    }>('/api/oauth/google/auth/', { code })
  }

  apple_login(code: string) {
    return this.httpClient.post<{
      access: string
      refresh: string
      user_id: number
      login: string
      sms_confirm?: boolean
    }>('/api/oauth/apple/auth/', { code })
  }

  get_geo_ip() {
    return this.httpClient.get<{
      is_rus: boolean
    }>('/api/system/geo/')
  }
}
